import { Dropdown, Field, TextAreaInput, TextInput } from 'Components/Core';
import { EquipmentTypesByQestId } from 'Components/Equipment/equipmentTypes';
import { ListFieldSection, PageSection, SectionGroup } from 'Components/Layout';
import { ListItemModel } from 'Data/ViewModels/ListItemModel';
import { useSelector } from 'react-redux';

import { DriverTestingSection } from './DriverTestingSection';

import { Severity } from 'Data/ViewModels/Core/Severity';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import { isRegularExpression, replaceNonPrintableChars, resolveSpecialCharacterSequences } from 'Lib/String';

const IndividualDriverAdmin: React.FC = () => {
  // get the list of Equipment Type id which supports Interfacing from the equipmentTypes.tsx file
  const ids: number[] = Object.keys(EquipmentTypesByQestId)
    .map((item) => parseInt(item))
    .filter((id) => EquipmentTypesByQestId[id].isInterfacingSupported);
  const equipmentTypes: ListItemModel[] = ids.map((id) => ({
    label: EquipmentTypesByQestId[id].name,
    value: id.toString(),
  }));
  const driver = useSelector((state: any) => state.admin.syncModel, deepEquals);

  const isRegexValid = useSelector(pipe((state: any) => state.admin.syncModel.Data?.Regex, isRegularExpression));

  const request = useSelector((state: any) => state.admin.syncModel.Data?.Request);
  const resolvedRequest = resolveSpecialCharacterSequences(request);

  return (
    <>
      <PageSection heading="Details">
        <SectionGroup>
          <ListFieldSection>
            <Field input={<TextInput />} label="Name" name="syncModel.Data.Name" maxLength={50} />
            <Field
              input={<Dropdown />}
              isClearable={false}
              label="Equipment Type"
              defaultValue="Balance"
              name="syncModel.Data.EquipmentType.Id"
              options={equipmentTypes}
              simpleValue
            />
            <Field input={<TextInput />} label="Equipment Make" name="syncModel.Data.EquipmentMake" maxLength={50} />
            <Field
              input={<TextInput />}
              label="Equipment Model No."
              name="syncModel.Data.EquipmentModelNo"
              maxLength={50}
            />
            <Field
              input={<TextInput />}
              label="Equipment Year of Manufacture"
              name="syncModel.Data.EquipmentYearOfManufacture"
              maxLength={50}
            />
          </ListFieldSection>
          <ListFieldSection wide>
            <Field
              input={<TextAreaInput />}
              label="Driver Description"
              name="syncModel.Data.Description"
              maxLength={1000}
              additionalInformationDialogContent="A brief description of the driver. Good details to include - the date that it was created and
              a brief overview of what the driver logic is trying to achieve as well as the expected data flow."
            />
          </ListFieldSection>
        </SectionGroup>
        <ListFieldSection wide heading="Driver Logic">
          <Field
            input={<TextInput />}
            label="Request Value"
            name="syncModel.Data.Request"
            maxLength={50}
            additionalInformationDialogContent={
              <>
                <p>
                  Enter the data that the equipment driver should send to the equipment item to request a value. Leave
                  blank if no request is to be made (e.g., the interface trigger is a manual input on the equipment
                  item).
                </p>
                <br />
                <p>Special character sequences can be entered to send non-printable characters. For example:</p>
                <ul>
                  <li>
                    <b>\r</b> will be sent as a carriage return
                  </li>
                  <li>\n will be sent as a line feed</li>
                  <li>
                    \xHH will be sent as a single character matching the ASCII code value HH (where H is a hexadecimal
                    digit)
                  </li>
                  <li>\\ will be sent as a single backlash</li>
                </ul>
              </>
            }
            messages={[
              ...(resolvedRequest !== request
                ? [
                    {
                      message: `Request will be sent as: ${replaceNonPrintableChars(resolvedRequest)}`,
                      severity: 'info' as Severity,
                    },
                  ]
                : []),
            ]}
          />
          <Field
            label="Regular Expression"
            input={<TextInput />}
            maxLength={1000}
            name="syncModel.Data.Regex"
            messages={isRegexValid ? [] : [{ severity: 'warning', message: 'The Regular Expression is not valid.' }]}
            additionalInformationDialogContent={
              <>
                <p>
                  A regular expression to match a subset of valid data from the stream that is returned, including an
                  expected Header and Terminator.
                </p>
                <br />
                <p>
                  The value will be taken by concatenating all capture groups within this regular expression into a
                  single string and parsing this as a number.
                </p>
              </>
            }
          />
        </ListFieldSection>
      </PageSection>
      <PageSection heading="Driver Testing">
        <DriverTestingSection driver={driver} />
      </PageSection>
    </>
  );
};

export default IndividualDriverAdmin;
